import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { UIButton, UIProgressBar } from "../../../../../ui";
import { clx } from "../../../../../../utils/Helpers";
import {
  CAPACITY_STATES,
  fillTemplate,
  formatAmount,
  getCapacityState,
  getExceededPercent,
} from "../../ChooseCrewSize/crewSizeUtils";

import "./CrewCapacityCounter.scss";

const PROGRESS_TONES = {
  [CAPACITY_STATES.below]: "primary",
  [CAPACITY_STATES.at]: "warning",
  [CAPACITY_STATES.above]: "error",
};

const CrewCapacityCounter = ({
  t,
  used,
  ceiling,
  crewSizeLabel,
  onGoToCrewSize,
  crewSizeLocked,
  attentionKey,
}) => {
  const state = getCapacityState(used, ceiling);
  const rootRef = useRef(null);

  // A blocked save scrolls to the counter; the key below replays the shake.
  useEffect(() => {
    if (attentionKey > 0) {
      rootRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
  }, [attentionKey]);
  // A locked crew size can't grow, so there is nothing to suggest at capacity.
  const message =
    state === CAPACITY_STATES.at && !crewSizeLocked
      ? t.atCapacity
      : state === CAPACITY_STATES.above
        ? fillTemplate(
            crewSizeLocked ? t.exceededCapacityLocked : t.exceededCapacity,
            { percent: formatAmount(getExceededPercent(used, ceiling)) },
          )
        : null;

  return (
    <div
      key={attentionKey}
      ref={rootRef}
      className={clx(
        "crew-capacity-counter",
        `crew-capacity-counter--${state}`,
        attentionKey > 0 &&
          state === CAPACITY_STATES.above &&
          "crew-capacity-counter--attention",
      )}
      role="status"
    >
      <div className="crew-capacity-counter__row">
        <Typography component="span" className="text-label-sm crew-capacity-counter__label">
          {t.totalApplicants}
        </Typography>
        <Typography component="span" className="text-label-sm crew-capacity-counter__value">
          {fillTemplate(t.capacitySummary, {
            used: formatAmount(used),
            ceiling: formatAmount(ceiling),
            crewSize: crewSizeLabel,
          })}
        </Typography>
      </div>

      <UIProgressBar
        value={ceiling > 0 ? Math.min(used / ceiling, 1) * 100 : 0}
        color={PROGRESS_TONES[state]}
        size="xs"
        aria-label={t.totalApplicants}
      />

      {message && (onGoToCrewSize || crewSizeLocked) && (
        <Typography component="p" className="text-body-xs crew-capacity-counter__message">
          {message}
        </Typography>
      )}
      {message && onGoToCrewSize && (
        <UIButton
          className="crew-capacity-counter__action"
          variant="text"
          size="small"
          type="button"
          startIcon={<ArrowBackIcon className="crew-capacity-counter__icon" />}
          onClick={onGoToCrewSize}
        >
          {t.goToCrewSizeStep}
        </UIButton>
      )}
    </div>
  );
};

CrewCapacityCounter.propTypes = {
  t: PropTypes.shape({
    totalApplicants: PropTypes.string,
    capacitySummary: PropTypes.string,
    atCapacity: PropTypes.string,
    exceededCapacity: PropTypes.string,
    exceededCapacityLocked: PropTypes.string,
    goToCrewSizeStep: PropTypes.string,
  }).isRequired,
  used: PropTypes.number.isRequired,
  ceiling: PropTypes.number.isRequired,
  crewSizeLabel: PropTypes.string.isRequired,
  /** Omit when there is no Crew Size step to go back to: the message and action are hidden. */
  onGoToCrewSize: PropTypes.func,
  /** The event waits on approval (PROLINE-4160): the crew size can't change, so an exceeded
   * total says so, with no action. */
  crewSizeLocked: PropTypes.bool,
  /** Bumped when a save is blocked above the ceiling: scrolls to the counter and shakes it. */
  attentionKey: PropTypes.number,
};

CrewCapacityCounter.defaultProps = {
  onGoToCrewSize: undefined,
  crewSizeLocked: false,
  attentionKey: 0,
};

export default CrewCapacityCounter;
